import { useState } from 'react';
import DemoLayout from '../components/DemoLayout';
import { Shield, Activity, AlertCircle, CheckCircle2, Clock, UserCircle, Mail, Phone, Linkedin, Building, Users, FileText, Globe, ShieldCheck, CreditCard, FolderKanban, ExternalLink, Download, ChevronDown, ChevronUp, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Xentra Demo Data - Matching preview version exactly
const xentraData = {
  company: {
    name: "Xentra",
    address: "06 Britannia Street, Leeds, LS1 2DZ",
    industry: "IT Services & Cybersecurity",
    employees: "10-50"
  },
  subscriptions: [
    {
      name: "Cyber STAX",
      tier: "Complete",
      status: "active",
      products: [
        'Secure Email Gateway',
        'M365 Impersonation Protection',
        'M365 Cloud Backup',
        'Threat Exposure Management',
        'Security Awareness Training',
        'Phishing Simulations',
        'Vulnerability Scanning',
        'Managed Endpoint Protection',
        'Managed Firewall',
        'Dark Web Monitoring',
        'Web Filtering & Monitoring',
        'Annual Cyber Audit',
        'Security Operations Centre (SOC)',
        '24/7 Monitoring & Remediation',
        '7-Day Ransomware Rollback',
        'Annual Penetration Testing',
        'Network Detection & Response',
        'Secure Remote Access',
        'Data Backup & Recovery'
      ],
      monthly_cost: 2500
    },
    {
      name: "Safeguarding",
      tier: "Complete",
      status: "active",
      products: [
        'Online Safety Monitoring',
        'Social Media Monitoring',
        'Keyword Alerts & Reporting',
        'Self-Harm & Suicide Prevention',
        'Bullying Detection',
        'Extremism & Radicalisation Alerts',
        'Parental Controls',
        'Screen Time Management',
        'Content Filtering',
        'Real-time Alerts',
        'Incident Reporting Dashboard',
        'Staff Training & Certification'
      ],
      monthly_cost: 500
    }
  ],
  securityStatus: {
    overall_score: 72,
    grade: "B",
    last_updated: "2024-12-15",
    file_name: "xentra_nist_assessment_2024.xlsx",
    risk_summary: { total: 45, mitigated: 32, escalate: 8, high: 5 },
    sections: [
      { 
        name: "Identify", 
        score: 75, 
        total_risks: 12, 
        mitigated_risks: 9,
        risks: [
          { name: "Asset inventory maintained and up to date", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Full asset inventory documented in IT management system", recommendations: "" },
          { name: "Business environment documented", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Business processes and dependencies mapped", recommendations: "" },
          { name: "Governance policies established", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Security policies reviewed annually", recommendations: "" },
          { name: "Risk assessment performed annually", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Last assessment completed December 2024", recommendations: "" },
          { name: "Risk management strategy defined", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Risk appetite and tolerance levels documented", recommendations: "" },
          { name: "Supply chain risk management", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "Third-party vendor assessment incomplete", recommendations: "Complete vendor security assessments for all critical suppliers by Q2 2025" },
          { name: "Data classification scheme implemented", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Data classified into Public, Internal, Confidential tiers", recommendations: "" },
          { name: "Critical systems identified", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Business-critical systems documented with RTO/RPO", recommendations: "" },
          { name: "Vulnerability management program", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Monthly vulnerability scans performed", recommendations: "" },
          { name: "Threat intelligence feeds active", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "No automated threat intelligence integration", recommendations: "Implement threat intelligence platform integration" },
          { name: "Cyber insurance coverage adequate", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "£5M cyber liability coverage in place", recommendations: "" },
          { name: "Regulatory compliance tracked", response: "No", risk_level: "High", risk_response: "Escalate", commentary: "GDPR compliance monitoring needs improvement", recommendations: "Implement automated compliance monitoring dashboard" }
        ]
      },
      { 
        name: "Protect", 
        score: 68, 
        total_risks: 15, 
        mitigated_risks: 10,
        risks: [
          { name: "Identity management controls", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Azure AD with SSO implemented", recommendations: "" },
          { name: "Multi-factor authentication enabled", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "MFA enforced for all users", recommendations: "" },
          { name: "Access control policies defined", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Role-based access control implemented", recommendations: "" },
          { name: "Network segmentation implemented", response: "No", risk_level: "High", risk_response: "Escalate", commentary: "Flat network architecture", recommendations: "Implement VLAN segmentation for critical systems" },
          { name: "Data-at-rest encryption", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "BitLocker enabled on all endpoints", recommendations: "" },
          { name: "Data-in-transit encryption", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "TLS 1.3 enforced for all communications", recommendations: "" },
          { name: "Endpoint protection deployed", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "EDR solution deployed across all endpoints", recommendations: "" },
          { name: "Email security gateway active", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Advanced email filtering with sandbox analysis", recommendations: "" },
          { name: "Security awareness training", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Quarterly training with phishing simulations", recommendations: "" },
          { name: "Privileged access management", response: "No", risk_level: "High", risk_response: "Escalate", commentary: "No PAM solution in place", recommendations: "Implement privileged access management solution" },
          { name: "Patch management automated", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "WSUS with 72-hour critical patch SLA", recommendations: "" },
          { name: "Mobile device management", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "BYOD devices not fully managed", recommendations: "Extend MDM coverage to all mobile devices" },
          { name: "Web application firewall", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "No WAF for public-facing applications", recommendations: "Deploy WAF for customer portal and public websites" },
          { name: "Physical security controls", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Access cards, CCTV, and visitor management in place", recommendations: "" },
          { name: "Secure configuration baselines", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "CIS benchmarks applied to servers and workstations", recommendations: "" }
        ]
      },
      { 
        name: "Detect & Respond", 
        score: 78, 
        total_risks: 10, 
        mitigated_risks: 8,
        risks: [
          { name: "Security monitoring active", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "24/7 SOC monitoring in place", recommendations: "" },
          { name: "SIEM deployed and configured", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Microsoft Sentinel with custom detection rules", recommendations: "" },
          { name: "Intrusion detection/prevention", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Network IDS/IPS at perimeter", recommendations: "" },
          { name: "Log retention policy defined", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "90-day retention for security logs", recommendations: "" },
          { name: "Incident response plan documented", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "IR playbooks for common scenarios", recommendations: "" },
          { name: "Incident response team defined", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "CSIRT with defined roles and escalation paths", recommendations: "" },
          { name: "Incident response tested", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "No tabletop exercise in past 12 months", recommendations: "Schedule quarterly tabletop exercises" },
          { name: "Forensic capabilities available", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Retainer with forensic investigation firm", recommendations: "" },
          { name: "Threat hunting performed", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "No proactive threat hunting program", recommendations: "Implement monthly threat hunting activities" },
          { name: "Communication plan for incidents", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Stakeholder communication templates ready", recommendations: "" }
        ]
      },
      { 
        name: "Recover", 
        score: 65, 
        total_risks: 8, 
        mitigated_risks: 5,
        risks: [
          { name: "Recovery plan documented", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "DRP documented for critical systems", recommendations: "" },
          { name: "Backup strategy implemented", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "3-2-1 backup strategy with offsite copies", recommendations: "" },
          { name: "Backup testing performed", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Monthly restore tests for critical data", recommendations: "" },
          { name: "Recovery time objectives defined", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "RTO/RPO defined for all critical systems", recommendations: "" },
          { name: "Disaster recovery tested", response: "No", risk_level: "High", risk_response: "Escalate", commentary: "Full DR test not performed in 18 months", recommendations: "Schedule annual full DR failover test" },
          { name: "Business continuity plan current", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "BCP last reviewed 2 years ago", recommendations: "Update BCP and conduct annual review" },
          { name: "Alternative site available", response: "Yes", risk_level: "Low", risk_response: "Mitigated", commentary: "Azure DR region configured", recommendations: "" },
          { name: "Post-incident review process", response: "No", risk_level: "Medium", risk_response: "Escalate", commentary: "Lessons learned not consistently documented", recommendations: "Implement formal post-incident review process" }
        ]
      }
    ]
  },
  accountManager: {
    name: "Sarah Johnson",
    job_title: "Senior Account Manager",
    email: "sarah.johnson@xentra.co.uk",
    phone: "+44 113 456 7890"
  },
  contacts: [
    { name: "Henry Doyle", email: "henry@xentra.co.uk", role: "CEO", phone: "+44 113 456 7891" },
    { name: "James Smith", email: "james@xentra.co.uk", role: "CTO", phone: "+44 113 456 7892" },
    { name: "Emma Wilson", email: "emma@xentra.co.uk", role: "IT Manager", phone: "+44 113 456 7893" }
  ],
  tickets: [
    { id: "TKT-001", subject: "Firewall configuration review", status: "Open", priority: "High", created_at: "2024-12-17" },
    { id: "TKT-002", subject: "New user onboarding", status: "In Progress", priority: "Medium", created_at: "2024-12-16" },
    { id: "TKT-003", subject: "Monthly security report", status: "Resolved", priority: "Low", created_at: "2024-12-15" }
  ],
  invoices: [
    { id: "INV-2024-012", amount: 2500, status: "Paid", date: "2024-12-01" },
    { id: "INV-2024-011", amount: 2500, status: "Paid", date: "2024-11-01" },
    { id: "INV-2024-010", amount: 2500, status: "Paid", date: "2024-10-01" }
  ],
  projects: [
    { name: "Security Assessment 2024", status: "In Progress", progress: 75 },
    { name: "Compliance Audit", status: "Completed", progress: 100 },
    { name: "Network Upgrade", status: "Planning", progress: 20 }
  ],
  resources: [
    { title: "Security Best Practices Guide", type: "PDF", category: "Documentation" },
    { title: "Incident Response Playbook", type: "PDF", category: "Procedures" },
    { title: "Employee Security Training", type: "Video", category: "Training" }
  ],
  darkWebAlerts: [
    { type: "Email Found", detail: "info@xentra.co.uk found in data breach", date: "2024-12-10", severity: "Medium" },
    { type: "Domain Mention", detail: "xentra.co.uk mentioned on dark web forum", date: "2024-11-28", severity: "Low" }
  ],
  vulnerabilities: [
    { name: "SSL Certificate Expiry", severity: "Medium", status: "Open", host: "mail.xentra.co.uk" },
    { name: "Outdated WordPress", severity: "High", status: "Fixed", host: "blog.xentra.co.uk" }
  ]
};

const getScoreColor = (score) => {
  if (score >= 80) return 'text-primary';
  if (score >= 60) return 'text-accent';
  if (score >= 40) return 'text-yellow-500';
  return 'text-destructive';
};

// Dashboard Page
export function DemoDashboard() {
  const totalProducts = xentraData.subscriptions.reduce((acc, sub) => acc + sub.products.length, 0);
  
  return (
    <DemoLayout title="Dashboard">
      <div className="space-y-6">
        {/* Active Subscriptions */}
        {xentraData.subscriptions.map((subscription, idx) => (
          <div key={idx} className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="h-6 w-6 text-primary" />
              <h3 className="text-2xl font-bold text-primary">{subscription.name}</h3>
              <span className="px-3 py-1 rounded-full text-xs bg-primary/20 text-primary">{subscription.tier.toUpperCase()}</span>
              <span className="px-3 py-1 rounded-full text-xs bg-primary/20 text-primary">ACTIVE</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {subscription.products.slice(0, 4).map((p, i) => (
                <div key={i} className="flex items-center gap-2 bg-background/50 rounded-md p-3 border border-border">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <span className="text-xs">{p}</span>
                </div>
              ))}
            </div>
            {subscription.products.length > 4 && (
              <p className="text-sm text-muted-foreground mt-3">+{subscription.products.length - 4} more products</p>
            )}
          </div>
        ))}

        {/* Security Status */}
        <div className="bg-gradient-to-br from-card to-primary/5 border-2 border-primary/30 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h3 className="text-lg font-bold">Security Status (NIST)</h3>
              <p className="text-xs text-muted-foreground">Based on NIST Cybersecurity Framework</p>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Overall</p>
              <p className={`text-4xl font-bold ${getScoreColor(xentraData.securityStatus.overall_score)}`}>
                {xentraData.securityStatus.overall_score}%
              </p>
            </div>
            {xentraData.securityStatus.sections.map((s, i) => (
              <div key={i}>
                <p className="text-sm text-muted-foreground mb-1">{s.name}</p>
                <p className={`text-2xl font-bold ${getScoreColor(s.score)}`}>{s.score}%</p>
              </div>
            ))}
          </div>
        </div>

        {/* Account Manager */}
        <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-secondary/20 flex items-center justify-center">
              <span className="text-xl font-bold text-secondary">SJ</span>
            </div>
            <div>
              <p className="text-xs text-secondary uppercase">Your Account Manager</p>
              <h3 className="text-xl font-bold">{xentraData.accountManager.name}</h3>
              <p className="text-sm text-muted-foreground">{xentraData.accountManager.job_title}</p>
              <div className="flex gap-4 mt-2">
                <span className="text-sm text-primary">{xentraData.accountManager.email}</span>
                <span className="text-sm">{xentraData.accountManager.phone}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-card border rounded-lg p-6 border-t-2 border-t-primary">
            <Activity className="h-8 w-8 text-primary mb-2" />
            <p className="text-3xl font-bold">{xentraData.subscriptions.length}</p>
            <p className="text-sm text-muted-foreground">Active Subscriptions</p>
          </div>
          <div className="bg-card border rounded-lg p-6 border-t-2 border-t-accent">
            <AlertCircle className="h-8 w-8 text-accent mb-2" />
            <p className="text-3xl font-bold">2</p>
            <p className="text-sm text-muted-foreground">Open Tickets</p>
          </div>
          <div className="bg-card border rounded-lg p-6 border-t-2 border-t-secondary">
            <CheckCircle2 className="h-8 w-8 text-secondary mb-2" />
            <p className="text-3xl font-bold">{totalProducts}</p>
            <p className="text-sm text-muted-foreground">Active Products</p>
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Subscription Page
export function DemoSubscription() {
  const totalMonthlyCost = xentraData.subscriptions.reduce((acc, sub) => acc + sub.monthly_cost, 0);
  
  return (
    <DemoLayout title="My Subscriptions">
      <div className="space-y-6">
        {/* Summary Card */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">{xentraData.subscriptions.length} Active Subscriptions</h3>
              <p className="text-sm text-muted-foreground">All products included below</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">£{totalMonthlyCost.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">total /month</p>
            </div>
          </div>
        </div>

        {/* Each Subscription */}
        {xentraData.subscriptions.map((subscription, idx) => (
          <div key={idx} className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Subscription Header */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="h-8 w-8 text-primary" />
                    <h2 className="text-2xl font-bold text-primary">{subscription.name}</h2>
                    <span className="px-3 py-1 rounded-full text-xs bg-primary/20 text-primary">{subscription.tier.toUpperCase()}</span>
                    <span className="px-3 py-1 rounded-full text-xs bg-primary/20 text-primary">ACTIVE</span>
                  </div>
                  <p className="text-muted-foreground">
                    {subscription.name === "Cyber STAX" ? "Complete cybersecurity protection suite" : "Online safety and safeguarding solution"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">£{subscription.monthly_cost.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">/month</p>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Active Products ({subscription.products.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {subscription.products.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg p-4 hover:bg-primary/10 transition-colors">
                    <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Subscription Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Billing Cycle</p>
            <p className="font-bold">Monthly</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Next Invoice</p>
            <p className="font-bold">1st January 2025</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Contract Start</p>
            <p className="font-bold">1st January 2024</p>
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Billing Page
export function DemoBilling() {
  return (
    <DemoLayout title="Billing">
      <div className="space-y-6">
        <div className="bg-card border rounded-lg">
          <div className="p-4 border-b"><h3 className="font-mono font-bold">Recent Invoices</h3></div>
          <div className="divide-y">
            {xentraData.invoices.map((inv) => (
              <div key={inv.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-mono font-bold">{inv.id}</p>
                  <p className="text-sm text-muted-foreground">{inv.date}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold">£{inv.amount}</p>
                  <span className="px-2 py-1 rounded-full text-xs bg-primary/20 text-primary">{inv.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Security Status Page - Matching the real customer portal exactly
export function DemoSecurityStatus() {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-primary/10 border-primary/20';
    if (score >= 60) return 'bg-accent/10 border-accent/20';
    if (score >= 40) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-destructive/10 border-destructive/20';
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade.startsWith('B')) return 'text-accent';
    if (grade.startsWith('C')) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getRiskLevelColor = (level) => {
    const l = (level || '').toLowerCase();
    if (l === 'low') return 'bg-primary/10 text-primary border-primary/20';
    if (l === 'medium') return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    if (l === 'high') return 'bg-destructive/10 text-destructive border-destructive/20';
    return 'bg-muted/10 text-muted-foreground border-muted/20';
  };

  const getSectionIcon = (name) => {
    switch (name) {
      case 'Identify': return '🔍';
      case 'Protect': return '🛡️';
      case 'Detect & Respond': return '⚡';
      case 'Recover': return '🔄';
      default: return '📋';
    }
  };

  const status = xentraData.securityStatus;

  return (
    <DemoLayout title="Security Status (NIST)">
      <div className="space-y-6">
        {/* Header with Overall Score */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full bg-background border-4 border-primary flex items-center justify-center">
                <span className={`text-3xl font-bold ${getGradeColor(status.grade)}`}>
                  {status.grade}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">Security Status (NIST)</h2>
                <p className="text-muted-foreground">
                  Based on NIST Cybersecurity Framework Assessment
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Last updated: {status.last_updated}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Overall Score</p>
              <p className={`text-5xl font-bold ${getScoreColor(status.overall_score)}`}>
                {status.overall_score}%
              </p>
            </div>
          </div>
        </div>

        {/* Risk Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Total Risks</p>
            <p className="text-2xl font-bold">{status.risk_summary.total}</p>
          </div>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Mitigated</p>
            <p className="text-2xl font-bold text-primary">{status.risk_summary.mitigated}</p>
          </div>
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Escalated</p>
            <p className="text-2xl font-bold text-accent">{status.risk_summary.escalate}</p>
          </div>
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">High Risk</p>
            <p className="text-2xl font-bold text-destructive">{status.risk_summary.high}</p>
          </div>
        </div>

        {/* NIST Sections */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold">NIST Framework Breakdown</h3>
          
          {/* Section Score Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {status.sections.map((section, idx) => (
              <div 
                key={idx} 
                className={`border rounded-lg p-4 ${getScoreBg(section.score)}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{getSectionIcon(section.name)}</span>
                  <span className="font-bold text-sm">{section.name}</span>
                </div>
                <p className={`text-3xl font-bold ${getScoreColor(section.score)}`}>
                  {section.score}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {section.mitigated_risks}/{section.total_risks} mitigated
                </p>
              </div>
            ))}
          </div>

          {/* Detailed Sections with Expandable Risks */}
          {status.sections.map((section, idx) => (
            <div key={idx} className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section.name)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getSectionIcon(section.name)}</span>
                  <div className="text-left">
                    <h4 className="font-bold">{section.name}</h4>
                    <p className="text-xs text-muted-foreground">
                      {section.total_risks} risks assessed • {section.mitigated_risks} mitigated
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xl font-bold ${getScoreColor(section.score)}`}>
                    {section.score}%
                  </span>
                  {expandedSections[section.name] ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </button>
              
              {expandedSections[section.name] && section.risks && (
                <div className="border-t border-border divide-y divide-border">
                  {section.risks.map((risk, rIdx) => (
                    <div key={rIdx} className="p-4 hover:bg-muted/5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {risk.response?.toLowerCase() === 'yes' ? (
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                            )}
                            <p className="text-sm font-medium">{risk.name}</p>
                          </div>
                          {risk.commentary && (
                            <p className="text-xs text-muted-foreground ml-6 mb-2">
                              {risk.commentary}
                            </p>
                          )}
                          {risk.recommendations && risk.recommendations !== '' && (
                            <div className="ml-6 mt-2 p-2 bg-accent/5 border border-accent/20 rounded text-xs">
                              <span className="font-bold text-accent">Recommendation:</span>{' '}
                              {risk.recommendations}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs border ${getRiskLevelColor(risk.risk_level)}`}>
                            {risk.risk_level || 'N/A'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            risk.risk_response?.toLowerCase() === 'mitigated' 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-accent/10 text-accent'
                          }`}>
                            {risk.risk_response || 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* File Info */}
        <div className="text-center text-xs text-muted-foreground">
          <FileText className="h-4 w-4 inline mr-1" />
          Assessment file: {status.file_name}
        </div>
      </div>
    </DemoLayout>
  );
}

// Dark Web Monitoring Page
export function DemoDarkWeb() {
  return (
    <DemoLayout title="Dark Web Monitoring">
      <div className="space-y-6">
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="h-8 w-8 text-accent" />
            <div>
              <h3 className="font-mono font-bold">Monitoring Active</h3>
              <p className="text-sm text-muted-foreground">Scanning for xentra.co.uk and associated emails</p>
            </div>
          </div>
        </div>
        <div className="bg-card border rounded-lg">
          <div className="p-4 border-b"><h3 className="font-mono font-bold">Recent Alerts</h3></div>
          <div className="divide-y">
            {xentraData.darkWebAlerts.map((alert, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-bold">{alert.type}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${alert.severity === 'High' ? 'bg-destructive/20 text-destructive' : alert.severity === 'Medium' ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'}`}>
                    {alert.severity}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{alert.detail}</p>
                <p className="text-xs text-muted-foreground mt-1">{alert.date}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Vulnerability Scanning Page
export function DemoVulnerability() {
  return (
    <DemoLayout title="Vulnerability Scanning">
      <div className="space-y-6">
        <div className="bg-card border rounded-lg">
          <div className="p-4 border-b"><h3 className="font-mono font-bold">Vulnerability Report</h3></div>
          <div className="divide-y">
            {xentraData.vulnerabilities.map((vuln, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-bold">{vuln.name}</p>
                    <p className="text-sm text-muted-foreground">{vuln.host}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${vuln.severity === 'High' ? 'bg-destructive/20 text-destructive' : 'bg-accent/20 text-accent'}`}>
                      {vuln.severity}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${vuln.status === 'Fixed' ? 'bg-primary/20 text-primary' : 'bg-yellow-500/20 text-yellow-500'}`}>
                      {vuln.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Email Security Page
export function DemoEmailSecurity() {
  return (
    <DemoLayout title="Email Security">
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card border rounded-lg p-6 text-center">
            <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-mono font-bold text-primary">PASS</p>
            <p className="text-sm text-muted-foreground">SPF Record</p>
          </div>
          <div className="bg-card border rounded-lg p-6 text-center">
            <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-mono font-bold text-primary">PASS</p>
            <p className="text-sm text-muted-foreground">DKIM</p>
          </div>
          <div className="bg-card border rounded-lg p-6 text-center">
            <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-mono font-bold text-primary">PASS</p>
            <p className="text-sm text-muted-foreground">DMARC</p>
          </div>
        </div>
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-mono font-bold mb-4">Email Security Score</h3>
          <div className="flex items-center gap-4">
            <p className="text-5xl font-mono font-bold text-primary">95%</p>
            <div className="flex-1">
              <div className="w-full bg-muted/20 rounded-full h-3">
                <div className="h-3 rounded-full bg-primary" style={{ width: '95%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Company Details Page
export function DemoCompany() {
  return (
    <DemoLayout title="Company Details">
      <div className="bg-card border rounded-lg p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Building className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-mono font-bold">{xentraData.company.name}</h2>
            <p className="text-muted-foreground">{xentraData.company.industry}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-muted-foreground">Address</p>
            <p className="font-mono">{xentraData.company.address}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Employees</p>
            <p className="font-mono">{xentraData.company.employees}</p>
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}

// Contacts Page
export function DemoContacts() {
  return (
    <DemoLayout title="Contacts">
      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b"><h3 className="font-mono font-bold">Team Contacts</h3></div>
        <div className="divide-y">
          {xentraData.contacts.map((contact, i) => (
            <div key={i} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="font-mono font-bold text-primary">{contact.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <div>
                  <p className="font-mono font-bold">{contact.name}</p>
                  <p className="text-sm text-muted-foreground">{contact.role}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-primary">{contact.email}</p>
                <p className="text-sm text-muted-foreground">{contact.phone}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// Support Page
export function DemoSupport() {
  return (
    <DemoLayout title="Support">
      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b"><h3 className="font-mono font-bold">Support Tickets</h3></div>
        <div className="divide-y">
          {xentraData.tickets.map((ticket) => (
            <div key={ticket.id} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-mono font-bold">{ticket.subject}</p>
                  <p className="text-xs text-muted-foreground">{ticket.id} • {ticket.created_at}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${ticket.priority === 'High' ? 'bg-destructive/20 text-destructive' : ticket.priority === 'Medium' ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'}`}>
                    {ticket.priority}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs ${ticket.status === 'Open' ? 'bg-accent/20 text-accent' : ticket.status === 'In Progress' ? 'bg-secondary/20 text-secondary' : 'bg-primary/20 text-primary'}`}>
                    {ticket.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// Projects Page
export function DemoProjects() {
  return (
    <DemoLayout title="Projects">
      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b"><h3 className="font-mono font-bold">Active Projects</h3></div>
        <div className="divide-y">
          {xentraData.projects.map((project, i) => (
            <div key={i} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono font-bold">{project.name}</p>
                <span className={`px-2 py-1 rounded-full text-xs ${project.status === 'Completed' ? 'bg-primary/20 text-primary' : project.status === 'In Progress' ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'}`}>
                  {project.status}
                </span>
              </div>
              <div className="w-full bg-muted/20 rounded-full h-2">
                <div className="h-2 rounded-full bg-primary" style={{ width: `${project.progress}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{project.progress}% complete</p>
            </div>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// Resources Page
export function DemoResources() {
  return (
    <DemoLayout title="Resources">
      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b"><h3 className="font-mono font-bold">Available Resources</h3></div>
        <div className="divide-y">
          {xentraData.resources.map((resource, i) => (
            <div key={i} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-mono font-bold">{resource.title}</p>
                  <p className="text-sm text-muted-foreground">{resource.category} • {resource.type}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" disabled>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}
